import { useQuery } from '@tanstack/react-query';
import { Skeleton, EmptyState, Pagination } from '@bbpos/ui';
import { useState } from 'react';
import { formatCurrency } from '@/utils/currency';
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { subscriptionService } from '@/services/subscription.service';
import type { SubscriptionChange } from '@/types/subscription';
import {
  GLASS_PANEL,
  changeTypeTone,
  cn,
  formatINRDate,
  formatINRDateTime,
  toneClasses,
} from './design-tokens';

interface ChangeHistoryTimelineProps {
  tenantId: string;
  heading?: string;
  pageSize?: number;
  /** When true, limit to the first N rows with no pagination (compact inline view). */
  compactLimit?: number;
}

const ACTION_LABEL: Record<string, string> = {
  UPGRADE: 'Upgraded',
  DOWNGRADE: 'Downgrade scheduled',
  RENEWAL: 'Renewed',
  CANCELLATION: 'Cancelled',
  TRIAL_START: 'Trial started',
  TRIAL_END: 'Trial ended',
};

function TimelineIcon({ type }: { type: string }) {
  const d =
    type === 'UPGRADE'
      ? 'M5 10l7-7m0 0l7 7m-7-7v18'
      : type === 'DOWNGRADE'
        ? 'M19 14l-7 7m0 0l-7-7m7 7V3'
        : type === 'RENEWAL'
          ? 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.582m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
          : type === 'CANCELLATION'
            ? 'M6 18L18 6M6 6l12 12'
            : type === 'TRIAL_START'
              ? 'M12 6v6l4 2m4-2a8 8 0 11-16 0 8 8 0 0116 0z'
              : 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function ChangeRow({ change, last }: { change: SubscriptionChange; last: boolean }) {
  // The currency the change was invoiced in, not the viewer's (QA SS4-001).
  const currency = toCurrencyCode(change.currency);
  const palette = toneClasses(changeTypeTone(change.change_type));
  const label = ACTION_LABEL[change.change_type] ?? change.change_type;

  return (
    <li className="relative pl-10 pb-5">
      {!last && (
        <span
          aria-hidden="true"
          className="absolute left-[18px] top-7 bottom-0 w-px bg-neutral-200 dark:bg-neutral-700/60"
        />
      )}
      <span
        className={cn(
          'absolute left-1 top-1 flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-white dark:ring-neutral-900',
          palette.icon,
        )}
      >
        <TimelineIcon type={change.change_type} />
      </span>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {label}
            {change.previous_plan && change.new_plan && change.change_type !== 'RENEWAL' && (
              <span className="ml-1 font-normal text-neutral-500 dark:text-neutral-400">
                · {change.previous_plan} → {change.new_plan}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {formatINRDateTime(change.created_at)}
          </p>
        </div>
        {change.amount != null && change.amount > 0 && (
          <p className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
            {formatCurrency(change.amount, currency)}
          </p>
        )}
      </div>
      {change.new_expires && (
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          New period ends {formatINRDate(change.new_expires)}
        </p>
      )}
      {change.notes && (
        <p className="mt-1 text-xs italic text-neutral-500 dark:text-neutral-400">
          &ldquo;{change.notes}&rdquo;
        </p>
      )}
    </li>
  );
}

export function ChangeHistoryTimeline({
  tenantId,
  heading = 'Change history',
  pageSize = 10,
  compactLimit,
}: ChangeHistoryTimelineProps) {
  const [page, setPage] = useState(1);
  const effectiveLimit = compactLimit ?? pageSize;

  const { data, isLoading } = useQuery({
    queryKey: ['subscription', 'history', tenantId, page, effectiveLimit],
    queryFn: () =>
      subscriptionService.getHistory(tenantId, { page, limit: effectiveLimit }),
    enabled: Boolean(tenantId),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className={cn(GLASS_PANEL, 'p-6')}>
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
            {heading}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Every plan transition, renewal, and lifecycle event.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          variant="section"
          title="No plan changes yet"
          description="Upgrades, downgrades, renewals and cancellations will appear on this timeline as they happen."
          icon={
            <div className="h-12 w-12 rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          }
        />
      ) : (
        <>
          <ol className="relative">
            {rows.map((c, i) => (
              <ChangeRow key={c.id} change={c} last={i === rows.length - 1} />
            ))}
          </ol>
          {!compactLimit && meta && meta.totalPages > 1 && (
            <div className="mt-4 flex justify-end">
              <Pagination
                currentPage={meta.page}
                totalPages={meta.totalPages}
                totalItems={meta.total}
                pageSize={meta.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
